import { Signal, computed } from '@angular/core';
import { Permission } from '../../api/app-types/permission.app';
import { RoleId } from '../../api/app-types/role.app';
import { User } from '../../api/app-types/user.app';
import { hasPermission } from './permission.func';

interface PositionShareAccessParams {
	userPermissions: Signal<Permission[]>;
	viewedUser: Signal<User | undefined>;
	currentUser: Signal<User | undefined>;
	currentUserObjectId: Signal<string | undefined>;
}

export function createPositionShareReadAccess(params: PositionShareAccessParams) {
	const canReadAllPositionShares = hasPermission(params.userPermissions, [
		'Users.PositionShares.Read',
		'Users.PositionShares.Edit',
	]);
	const canReadOwnPositionShares = hasPermission(params.userPermissions, 'Users.PositionShares.Read.Own');

	const isOwnUserDetail = computed(() => {
		const viewedUser = params.viewedUser();
		const currentObjectId = params.currentUserObjectId();
		if (!viewedUser?.objectId || !currentObjectId) return false;
		return String(viewedUser.objectId) === String(currentObjectId);
	});

	const isTeamLeader = computed(() => {
		const roleId = params.currentUser()?.roles?.id;
		return roleId === RoleId.IFD_TEAM_LEADER;
	});

	const isSameIntegrationOffice = computed(() => {
		const currentOfficeId = params.currentUser()?.integrationServiceOffice?.id;
		const viewedOfficeId = params.viewedUser()?.integrationServiceOffice?.id;
		if (!currentOfficeId || !viewedOfficeId) return false;
		return String(currentOfficeId) === String(viewedOfficeId);
	});

	const canReadPositionShares = computed(
		() =>
			canReadAllPositionShares() ||
			(isOwnUserDetail() && canReadOwnPositionShares()) ||
			(isTeamLeader() && isSameIntegrationOffice()),
	);

	return {
		canReadAllPositionShares,
		canReadOwnPositionShares,
		isOwnUserDetail,
		canReadPositionShares,
	};
}
